import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { reorderItem } from '~src/app/actions/ReorderItem';

import { Formatters,Messages } from '../../../codegen/Localize';
import { SpaceContainerStyles } from '../../../style/SpaceContainerStyles';
import { Keys } from '../../../ts/helpers/Keys';
import { getKeyForExplorableItem } from '../../../ts/models/ContentType';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IExplorableItemTypeInfo } from '../../../ts/models/resourceInfoParts';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { ToasterService } from '../../../ts/services/ToasterService';
import { addToContentSelection,removeFromContentSelection } from '../../actions/spaces/ContentExplorerSelectionActions';
import { requestSpaceContentsMissingSlice } from '../../actions/spaces/RequestSpaceContentsActions';
import { CardPositionContext, ContentInfoContext } from '../../contexts/TelemetryContexts';
import { ItemsSet } from '../../ItemsSet';
import { selectContentFromAppState, selectSelectedContentItems } from '../../selectors/ContentSelectors';
import { selectContentSort } from '../../selectors/ContentSortSelectors';
import { selectSpaceInfo } from '../../selectors/SpaceInfoSelectors';
import { AppState } from '../../state/AppState';
import { isFavoritesSpace,SpaceInfo } from '../../state/SpaceInfo';
import { MapDispatchToProps,MapStateToProps } from '../../utils/ConnectUtils';
import { CardScroller } from './CardScroller';
import { CardViewStyles, TOTAL_CARD_HEIGHT, TOTAL_CARD_WIDTH, } from './CardViewStyles';
import { ConnectedContentCardWrapper } from './ConnectedContentCardWrapper';

export const SpaceBetweenCardsInPx = 18;
export const CardContainerHorizontalPadding = SpaceContainerStyles.GlobalSpaceHorizontalPadding - SpaceBetweenCardsInPx / 2;

export interface CardViewOwnProps {
  readonly onCardScrollerLayoutUpdated?: () => void;
  readonly showActionMenu: boolean;
  readonly showSelection: boolean;
  readonly reorderItemAction: (fromIndex: number, toIndex: number, spaceInfo: SpaceInfo) => void;
  readonly allowDragAndDrop: boolean;
  readonly reorderAccessibilityInstructions: string;
}

export interface CardViewStateProps {
  readonly selectedItems: ItemsSet<IExplorableItemTypeInfo>;
  readonly contentSort: ReadonlyArray<Readonly<IOrderClause>>;
  readonly isBusyFetchingItems: boolean;
  readonly spaceInfo: SpaceInfo;
  readonly items: ReadonlyArray<IExplorableItemTypeInfo>;
  readonly inFavoritesSpace: boolean;
}

export interface CardViewDispatchProps {
  readonly deselectItem: (item: IContentInfo) => void;
  readonly requestSpaceContentsMissingSlice: (start: number, end: number) => void;
  readonly selectItem: (item: IContentInfo) => void;
  readonly reorderItem: (fromIndex: number, toIndex: number, spaceInfo: SpaceInfo) => void;
}

export type CardViewProps = CardViewOwnProps & CardViewStateProps & CardViewDispatchProps;

export interface CardViewState {
  readonly dragInProgress: boolean;

  readonly reorderingByKeyboard: boolean;
  readonly reordering_startIndex: number;
  readonly reordering_cardRef: any;
}

export class CardView extends React.Component<CardViewProps, CardViewState> {
  private unmounted: boolean = false;
  private index: number;

  public constructor(props: CardViewProps) {
    super(props);
    this.state = {
      dragInProgress: false,
      reorderingByKeyboard: false,
      reordering_startIndex: undefined,
      reordering_cardRef: undefined
    };
    this.index = undefined;
  }

  public render() {
    if (this.props.isBusyFetchingItems) {
      // If the initial render (which doesn't call shouldComponentUpdate) is still busy fetching items
      // Don't render anything.
      return null;
    }

    const scroller = <CardScroller items={this.props.items}
      requestMissingSlice={this.props.requestSpaceContentsMissingSlice}
      renderFetchedCard={this.renderCard}
      renderUnfetchedCard={UnfetchedCard}
      getKeyForItem={getKeyForExplorableItem}
      itemHeightInPx={TOTAL_CARD_HEIGHT}
      itemWidthInPx={TOTAL_CARD_WIDTH}
      itemSpacingInPx={SpaceBetweenCardsInPx}
      sectionHorizontalPaddingInPx={CardContainerHorizontalPadding}
      contentSort={this.props.contentSort}
      onLayoutUpdated={this.props.onCardScrollerLayoutUpdated}
      onKeyDown={this.props.allowDragAndDrop ? this.keydownHandler : null}
    />;


    return scroller;
  }

  private renderCard = (props: { item: IContentInfo, index: number, row: number }): JSX.Element => {
    const { item, index } = props;
    return (
      <ContentInfoContext.Provider value={item}>
        <CardPositionContext.Provider value={index + 1}>
          <ConnectedContentCardWrapper
            content={item}
            toggleSelection={this.props.showSelection && this.toggleSelection}
            selected={this.props.showSelection && this.isItemSelected(item)}
            showActionMenu={this.props.showActionMenu}
            itemIndex={index}
            row={props.row}
            forDragPreview={false}
            dragInProgress={this.state.dragInProgress}
            setIsDragging={this.setIsDragging}
            keyboardReorderInProgress={this.state.reorderingByKeyboard && this.props.allowDragAndDrop}
            setCurrentlyFocusedCardInfo={this.setCurrentlyFocusedCardInfo}
            reordering_ariaText={this.getReorderingAriaText()}
            reorderItemAction={this.props.reorderItemAction}
            allowDragAndDrop={this.props.allowDragAndDrop}
          />
        </CardPositionContext.Provider>
      </ContentInfoContext.Provider>
    );
  }

  // When filtering on content type, the content type property will cause a rerender
  // however we won't have the updated items yet so do not want to rerender
  // So only rerender if we are told we aren't busy fetching new items.
  // Because Explorer (parent) is a pure component, we won't re render if items haven't changed
  public shouldComponentUpdate(nextProps: CardViewProps): boolean {
    return !nextProps.isBusyFetchingItems;
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  private isItemSelected = (content: IContentInfo): boolean => {
    const key = getKeyForExplorableItem(content);
    return !!this.props.selectedItems[key];
  }

  private toggleSelection = (content: IContentInfo): void => {
    this.isItemSelected(content) ? this.props.deselectItem(content) : this.props.selectItem(content);
  }

  private setIsDragging = (isDragging: boolean) => {
    this.setState({ dragInProgress: isDragging });
  }

  private setCurrentlyFocusedCardInfo = (startIndex: number, ref: any): void => {
    this.updateState({
      reordering_startIndex: startIndex,
      reordering_cardRef: ref,
    });
    this.index = startIndex;
  }

  private getReorderingAriaText(): string {
    if (!this.props.allowDragAndDrop) {
      return undefined;
    }

    if (this.state.reorderingByKeyboard &&
      !_.isUndefined(this.state.reordering_startIndex) &&
      !_.isUndefined(this.index)) {
      // add one to the indices because users don't want 0-based indexing
      return Messages.favoritesReorderAccessibilityInProgress({
        ORIGINAL_POSITION: Formatters.formatCount(this.state.reordering_startIndex + 1),
        NEW_POSITION: Formatters.formatCount(this.index + 1)
      });
    } else {
      return this.props.reorderAccessibilityInstructions;
    }
  }

  private updateState<K extends keyof CardViewState>(newState: Pick<CardViewState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }


  private keydownHandler = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const startIndex = this.state.reordering_startIndex;
    switch (event.which) {
      case Keys.ArrowUp:
        {
          if ((event.altKey || event.metaKey) && !this.state.reorderingByKeyboard) {
            event.preventDefault();  // don't scroll
            // start keyboard reorder
            this.updateState({ reorderingByKeyboard: true });
          }
          return;
        }
      case Keys.ArrowDown:
        {
          if ((event.altKey || event.metaKey) && this.state.reorderingByKeyboard) {
            event.preventDefault();  // don't scroll

            if (this.index !== startIndex) {
              // end keyboard reorder, and actually call reorder on the BE
              this.props.reorderItemAction(startIndex, this.index - 1, this.props.spaceInfo);
            }

            // end reordering but update the start index in case user wants to pick up and reorder the card again
            // (without removing focus first, which triggers the updates to the startIndex and index)
            this.updateState({ reorderingByKeyboard: false, reordering_startIndex: this.index });

            removeToastsAfterReordering();
          }
          return;
        }
      case Keys.ArrowRight:
        {
          const newIndex = Math.min(this.index + 1, this.props.items.length - 1);
          this.reorderForArrowKeyPress(newIndex, newIndex, this.state.reordering_cardRef);
          return;
        }
      case Keys.ArrowLeft:
        {
          const indexToMoveAfter = Math.max(
            this.index - 2, // to move back 1, we need to move AFTER two indeces before
            -1); // -1 means beginning of list
          const newIndex = indexToMoveAfter + 1;

          this.reorderForArrowKeyPress(indexToMoveAfter, newIndex, this.state.reordering_cardRef);
          return;
        }

      case Keys.Tab:
      case Keys.Escape:
        {
          if (!this.state.reorderingByKeyboard) {
            return;
          }

          if (startIndex < this.index) {
            this.props.reorderItem(this.index, startIndex - 1, this.props.spaceInfo);
          } else {
            this.props.reorderItem(this.index, startIndex, this.props.spaceInfo);
          }

          removeToastsAfterReordering();

          if (this.state.reordering_cardRef) {
            this.state.reordering_cardRef.scrollIntoView({ block: 'nearest' }) ;
          }
          this.updateState({ reorderingByKeyboard: false });

          // reset the index in case user wants to pick up and reorder the card again (without removing focus first, which
          // triggers the updates to the startIndex and index)
          this.index = this.state.reordering_startIndex;
        }
    }
  };

  private reorderForArrowKeyPress(
    indexToMoveAfter: number,
    newIndex: number,
    ref: any) {
    if (!ref || !this.state.reorderingByKeyboard) {
      return;
    }

    if (newIndex === this.index) {
      return;
    }

    // Update the index before reordering since getReorderingAriaText uses it to give
    // a description of what move just happened to the card, which gets done right after
    // this.props.reorderItem is dispatched.
    const prevIndex = this.index;
    this.index = newIndex;

    this.props.reorderItem(prevIndex, indexToMoveAfter, this.props.spaceInfo);
    if (ref) {
      ref.scrollIntoView({ block: 'nearest' });
    }
  }
}

function UnfetchedCard() {
  return (
    <div className={`tb-unfetched-card ${CardViewStyles.Card}`}>
      <div className={CardViewStyles.Thumbnail} />
    </div>
  );
}

function removeToastsAfterReordering() {
  // Undoing un-favoriting an item from a toast doesn't work after reordering favorites because
  // the favorited item is in the wrong place. We also can't simply refresh the whole page when
  // undoing un-favoriting because the SOLR Favorites Core is slow and so we can't show the updates
  // fast enough. Instead, just remove the toasts after reordering.
  const toasterService = ToasterService.instance;
  toasterService.closeAllToastsNotMarkedAsKeep();
}

const mapStateToProps: MapStateToProps<CardViewStateProps> = (state: AppState) => {
  const spaceInfo: SpaceInfo = selectSpaceInfo(state);
  return {
    isBusyFetchingItems: state.contentExplorer.content.isBusy,
    isSelectionBusy: state.contentExplorer.selection.isBusy,
    items: selectContentFromAppState(state),
    selectedItems: selectSelectedContentItems(state),
    contentSort: selectContentSort(state),
    spaceInfo: spaceInfo,
    inFavoritesSpace: isFavoritesSpace(spaceInfo),
  };
};

const mapDispatchToProps: MapDispatchToProps<CardViewDispatchProps> = (dispatch) => bindActionCreators({
  selectItem: addToContentSelection,
  deselectItem: removeFromContentSelection,
  requestSpaceContentsMissingSlice: requestSpaceContentsMissingSlice,
  reorderItem: reorderItem,
}, dispatch);

export const ConnectedCardView: React.ComponentClass<CardViewOwnProps> =
  connect(mapStateToProps, mapDispatchToProps)(CardView);
